<template>
  <CustomTitleBar title="账单" />
  <div class="page-content">
    <!-- 账单月份统计 -->
    <div class="bill-header">
      <div class="header-row">
        <div class="month" @click="showCalendar = true">
          {{ currentMonth }}
          <van-icon name="arrow-down" />
        </div>
        <div class="amount-info">
          <span class="income">收入：<span class="money">{{ totalIncome }}鲸油</span></span>
          <span class="expense">支出：<span class="money">{{ totalExpense }}鲸油</span></span>
        </div>
      </div>
    </div>

    <!-- 添加日期选择器 -->
    <van-popup v-model:show="showCalendar" position="bottom">
      <van-date-picker v-model="currentDate" title="选择年月" :min-date="new Date(2020, 0)" :max-date="new Date()"
        :columns-type="['year', 'month']" @confirm="onMonthSelect" @cancel="showCalendar = false" />
    </van-popup>

    <!-- 账单列表 -->
    <div class="bill-list">
      <van-list v-model:loading="loading" :finished="finished" finished-text="没有更多了" @load="onLoad"
        :immediate-check="true" :offset="10">
        <!-- 添加状态显示 -->
        <div v-if="billList.length === 0 && finished" class="empty-state">
          <van-empty description="暂无账单记录" />
        </div>

        <div class="bill-item" v-for="(item, index) in billList" :key="index">
          <div class="left">
            <div class="title">{{ item.title }}</div>
            <div class="time">{{ formatTime(item.timestamp) }}</div>
          </div>
          <div class="amount" :class="{ 'income': item.type === 0, 'expense': item.type === 1 }">
            {{ item.type === 0 ? '+' : '-' }}{{ item.amount }}
          </div>
        </div>
      </van-list>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import CustomTitleBar from "@/components/CustomTitleBar/index.vue"
import { showToast } from 'vant'
import { getBillList } from '@/api/wallet'

interface BillItem {
  title: string
  amount: number
  type: number
  timestamp: number
}
// 分页参数
const current = ref(1)
const size = ref(10)
const start = ref<number>(0)
const end = ref<number>(0)

// 列表相关
const loading = ref(false)
const finished = ref(false)
const billList = ref<BillItem[]>([])

// 统计数据
const currentMonth = ref('')
const totalIncome = ref(0)
const totalExpense = ref(0)

// 添加日期选择相关变量
const showCalendar = ref(false)
const currentDate = ref<string[]>([
  String(new Date().getFullYear()),
  String(new Date().getMonth() + 1).padStart(2, '0')
])

// 添加初始化月份的函数
const initCurrentMonth = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  currentMonth.value = `${year}年${month}月`

  // 设置月初时间戳
  const startDate = new Date(year, month - 1, 1)
  start.value = startDate.getTime()

  // 设置月末时间戳
  const endDate = new Date(year, month, 0, 23, 59, 59)
  end.value = endDate.getTime()
}

// 修改日期选择器相关的类型定义和处理
interface DatePickerValue {
  selectedValues: [string, string]
}

// 修改日期选择处理函数
const onMonthSelect = (value: DatePickerValue) => {
  const [year, month] = value.selectedValues
  currentMonth.value = `${year}年${month}月`
  showCalendar.value = false

  // 设置月初时间戳
  const startDate = new Date(Number(year), Number(month) - 1, 1)
  start.value = startDate.getTime()

  // 设置月末时间戳
  const endDate = new Date(Number(year), Number(month), 0, 23, 59, 59)
  end.value = endDate.getTime()

  // 重置列表数据和状态
  current.value = 1
  billList.value = []
  finished.value = false
  loading.value = false

  // 手动触发数据加载
  onLoad()
}


const onLoad = () => {
  loading.value = true

  getBillList({
    current: current.value,
    size: size.value,
    start: start.value,
    end: end.value
  }).then((res) => {
    if (res.code === 200) {
      const { income, expenditure, pageUtils } = res.data

      // 更新总收支
      totalIncome.value = income || 0
      totalExpense.value = expenditure || 0

      // 检查是否有数据
      if (!pageUtils.list || pageUtils.list.length === 0) {
        finished.value = true
        loading.value = false
        return
      }

      // 处理列表数据
      const formattedList = pageUtils.list.map((item: BillItem) => ({
        ...item,
        time: formatTime(item.timestamp)
      }))

      billList.value.push(...formattedList)

      // 判断是否加载完成
      if (pageUtils.currPage >= pageUtils.totalPage) {
        finished.value = true
      }

      current.value = pageUtils.currPage + 1
    } else {
      showToast('获取账单列表失败')
      finished.value = true
    }
    loading.value = false
  }).catch((error) => {
    console.error('获取账单列表失败:', error)
    showToast(`获取账单列表失败: ${error.message || '未知错误'}`)
    loading.value = false
    finished.value = true
  })
}

const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = date.getHours().toString().padStart(2, '0');
  const minute = date.getMinutes().toString().padStart(2, '0');

  return `${month}/${day} ${hour}:${minute}`;
}

onMounted(() => {
  // 初始化当前月份
  initCurrentMonth()
  // 加载数据
  onLoad()
})
</script>

<style lang="scss" scoped>
.page-content {
  padding-top: 44px;
  background: #f5f5f5;
  overflow: hidden;
}

.bill-header {
  background: #fff;
  padding: 15px;
  margin-bottom: 10px;

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .month {
    font-size: 14px;
    color: #0256FF;
    display: flex;
    align-items: center;
    cursor: pointer;

    .van-icon {
      margin-left: 5px;
    }
  }

  .amount-info {
    display: flex;
    gap: 15px;
    font-size: 13px;
    color: #767676;

    .money {}
  }
}

.bill-list {
  height: calc(100vh - 120px);
  overflow-y: auto;
  background: #fff;

  .empty-state {
    padding: 40px 0;
    text-align: center;
  }

  .bill-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #f5f5f5;

    .left {
      .title {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 5px;
      }

      .time {
        font-size: 12px;
        color: #767676;
      }
    }

    .amount {
      font-size: 16px;
      color: #FA5151;

      &.income {
        color: #2CC069;
      }
    }
  }
}
</style>